<template>
  <div class="designated-audit-container">
    <!-- 顶部标题与说明 -->
    <div class="section-title">
      <span>{{ $t('podVerification.tabDesignatedAudit') }}</span>
    </div>
    <div class="simple-tip">{{ $t('podVerification.auditIntro') }}</div>

    <!-- 上传区 -->
    <div class="upload-card">
      <div class="upload-line">
        <div class="upload-main">
          <div class="upload-title">{{ $t('podVerification.uploadTitle') }}</div>
          <div class="upload-desc">{{ $t('podVerification.uploadDesc') }}</div>
        </div>
        <el-button icon="el-icon-download" size="small" @click="downloadTemplate">
          {{ $t('podVerification.downloadTemplate') }}
        </el-button>
        <el-upload
          ref="uploadRef"
          :auto-upload="false"
          :on-change="handleFileChange"
          :show-file-list="false"
          accept=".xlsx,.xls,.csv"
          action="#"
          style="display: inline-block"
        >
          <el-button icon="el-icon-upload2" size="small" type="primary">
            {{ $t('podVerification.selectFile') }}
          </el-button>
        </el-upload>
      </div>

      <!-- 文件选择后显示 -->
      <div v-if="fileInfo.name" class="file-info">
        <div>
          <div class="file-name">{{ fileInfo.name }}</div>
          <div class="file-meta">{{ $t('podVerification.fileReadTip') }}</div>
        </div>
        <div style="display: flex; align-items: center; gap: 10px">
          <span :style="{ color: precheckStateColor }" class="precheck-state">{{
            precheckState
          }}</span>
          <el-button size="mini" @click="resetFile">{{ $t('podVerification.reselect') }}</el-button>
        </div>
      </div>

      <!-- 预提交校验结果区 -->
      <div v-if="showPrecheck" class="precheck-box">
        <div class="precheck-title">
          <strong>{{ $t('podVerification.precheckResult') }}</strong>
          <span class="precheck-tip">{{ $t('podVerification.precheckTip') }}</span>
        </div>

        <div class="precheck-stats">
          <div class="precheck-stat ok">
            <div class="label">{{ $t('podVerification.auditable') }}</div>
            <div class="value">{{ formatNumber(precheckData.auditable) }}</div>
          </div>
          <div class="precheck-stat warn">
            <div class="label">{{ $t('podVerification.notSigned') }}</div>
            <div class="value">{{ formatNumber(precheckData.notSigned) }}</div>
          </div>
          <div class="precheck-stat warn">
            <div class="label">{{ $t('podVerification.signedNoPod') }}</div>
            <div class="value">{{ formatNumber(precheckData.noPOD) }}</div>
          </div>
          <div class="precheck-stat bad">
            <div class="label">{{ $t('podVerification.notExist') }}</div>
            <div class="value">{{ formatNumber(precheckData.notExist) }}</div>
          </div>
          <div class="precheck-stat">
            <div class="label">{{ $t('podVerification.duplicate') }}</div>
            <div class="value">{{ formatNumber(precheckData.duplicate) }}</div>
          </div>
        </div>

        <div class="precheck-actions">
          <el-button :disabled="!taskId" size="small" @click="exportPrecheckDetail">
            {{ $t('podVerification.exportPrecheck') }}
          </el-button>
          <el-button
            :disabled="!canStartAudit"
            :loading="submitting"
            size="small"
            type="primary"
            @click="startAudit"
          >
            {{
              submitting
                ? $t('podVerification.submitting')
                : $t('podVerification.startAuditCount', {
                  count: formatNumber(precheckData.auditable)
                })
            }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 审核任务列表 -->
    <div class="section-title" style="margin-top: 20px">{{ $t('podVerification.auditTask') }}</div>
    <div class="task-toolbar">
      <el-form :inline="true" size="small" style="display: flex; align-items: flex-end; gap: 10px">
        <el-form-item :label="$t('podVerification.taskNo')" style="margin-bottom: 0">
          <el-input
            v-model="taskQuery.taskNo"
            :placeholder="$t('podVerification.inputTaskNo')"
            clearable
            style="width: 200px"
            @keyup.enter.native="searchTasks"
          />
        </el-form-item>
        <el-form-item :label="$t('podVerification.taskStatus')" style="margin-bottom: 0">
          <el-select
            v-model="taskQuery.status"
            :placeholder="$t('podVerification.all')"
            clearable
            style="width: 150px"
          >
            <el-option :label="$t('podVerification.all')" value="" />
            <el-option :label="$t('podVerification.auditing')" value="AUDITING" />
            <el-option :label="$t('podVerification.completed')" value="COMPLETED" />
          </el-select>
        </el-form-item>
        <el-button
          icon="el-icon-search" size="small" type="primary"
          @click="searchTasks"
        >{{ $t('podVerification.search') }}
        </el-button>
        <el-button icon="el-icon-refresh-left" size="small" @click="resetTaskQuery">
          {{ $t('podVerification.reset') }}
        </el-button>
      </el-form>
    </div>

    <el-table
      v-loading="taskLoading" :data="taskList" border
      size="small" style="width: 100%"
    >
      <el-table-column :label="$t('podVerification.taskNo')" min-width="160" prop="taskNo" />
      <el-table-column
        :label="$t('podVerification.uploadTime')"
        min-width="160"
        prop="createTime"
      />
      <el-table-column
        :label="$t('podVerification.uploadCount')"
        align="center"
        prop="auditTotal"
        width="100"
      >
        <template slot-scope="scope">{{ formatNumber(scope.row.auditTotal) }}</template>
      </el-table-column>
      <el-table-column
        :label="$t('podVerification.auditedCount')"
        align="center"
        prop="auditedCount"
        width="100"
      >
        <template slot-scope="scope">{{ formatNumber(scope.row.auditedCount) }}</template>
      </el-table-column>
      <el-table-column :label="$t('podVerification.auditProgress')" min-width="180">
        <template slot-scope="scope">
          <div class="progress-wrap">
            <el-progress
              :percentage="scope.row.progress || 0"
              :show-text="false"
              :stroke-width="8"
              color="#17b75a"
              style="width: 110px"
            />
            <span>{{ scope.row.progress || 0 }}%</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column :label="$t('podVerification.taskStatus')" align="center" width="100">
        <template slot-scope="scope">
          <span :class="['status', scope.row.status === '已完成' ? 'green' : 'blue']">
            {{ getStatusText(scope.row.status) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('podVerification.operation')" align="center" width="150">
        <template slot-scope="scope">
          <el-button type="text" @click="showResult(scope.row)">{{ $t('podVerification.viewResult') }}
          </el-button>
          <el-button type="text" @click="exportTaskResult(scope.row)">{{ $t('podVerification.export') }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div style="display: flex; justify-content: flex-end; margin-top: 12px">
      <el-pagination
        :current-page.sync="taskQuery.pageNumber"
        :page-size.sync="taskQuery.pageSize"
        :page-sizes="[10, 20, 50, 100]"
        :total="taskTotal"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @current-change="getTaskList"
        @size-change="handleTaskSizeChange"
      />
    </div>

    <!-- 查看结果弹窗 -->
    <el-dialog
      :title="$t('podVerification.auditResultTitle')"
      :visible.sync="resultDialogVisible"
      custom-class="result-modal"
      top="5vh"
      width="82%"
    >
      <div class="result-modal-header">
        <div>
          <span style="font-size: 12px; color: #8d98a6">{{
            $t('podVerification.taskNoLabel')
          }}</span>
          <strong style="font-size: 12px">{{ currentTaskNo }}</strong>
        </div>
        <div class="result-filter">
          <el-input
            v-model="resultQuery.keyword"
            :placeholder="$t('podVerification.searchWaybill')"
            clearable
            size="small"
            style="width: 280px"
            @input="handleKeywordInput"
            @keyup.enter.native="handleKeywordSearchNow"
          />
          <el-select
            v-model="resultQuery.result"
            :placeholder="$t('podVerification.allResult')"
            clearable
            size="small"
            style="width: 130px"
            @change="handleResultFilter"
          >
            <el-option :label="$t('podVerification.qualified')" :value="1" />
            <el-option :label="$t('podVerification.unqualified')" :value="0" />
            <el-option :label="$t('podVerification.pending')" value="PENDING" />
          </el-select>
          <el-button size="small" type="primary" @click="exportResults">
            {{ $t('podVerification.exportAuditResult') }}
          </el-button>
        </div>
      </div>

      <el-table
        v-loading="resultLoading" :data="resultList" border
        size="small"
      >
        <el-table-column
          :label="$t('podVerification.waybillNumber')"
          min-width="170"
          prop="waybillNumber"
        />
        <el-table-column :label="$t('podVerification.signatureResult')" align="center" width="110">
          <template slot-scope="scope">
            <span :class="['tag', getTagClass(scope.row.signatureResult)]">{{
              getResultText(scope.row.signatureResult)
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('podVerification.signatureFailReason')"
          min-width="180"
          prop="signatureReason"
        />
        <el-table-column :label="$t('podVerification.photoResult')" align="center" width="110">
          <template slot-scope="scope">
            <span :class="['tag', getTagClass(scope.row.photoResult)]">{{
              getResultText(scope.row.photoResult)
            }}</span>
          </template>
        </el-table-column>
        <el-table-column
          :label="$t('podVerification.photoFailReason')"
          min-width="180"
          prop="photoReason"
        />
        <el-table-column
          :label="$t('podVerification.auditTime')"
          min-width="160"
          prop="auditTime"
        />
        <el-table-column :label="$t('podVerification.operation')" align="center" width="80">
          <template slot-scope="scope">
            <el-button type="text" @click="openDetail(scope.row)">{{ $t('podVerification.detail') }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <div class="pager">
        <span>{{ $t('podVerification.totalRecords', { count: formatNumber(resultTotal) }) }}</span>
        <el-pagination
          :current-page.sync="resultQuery.pageNumber"
          :page-size.sync="resultQuery.pageSize"
          :page-sizes="[10, 20, 50]"
          :total="resultTotal"
          background
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="getResultList"
          @size-change="handleResultSizeChange"
        />
      </div>
    </el-dialog>

    <!-- 详情弹窗 -->
    <el-dialog
      :title="$t('podVerification.detailTitle')"
      :visible.sync="detailDialogVisible"
      width="600px"
    >
      <el-descriptions :column="1" :label-style="detailLabelStyle" border size="small">
        <el-descriptions-item :label="$t('podVerification.waybillNumber')">{{ detailData.waybillNumber }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('podVerification.signatureResult')">
          <span :class="['tag', getTagClass(detailData.signatureResult)]">{{
            getResultText(detailData.signatureResult)
          }}</span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('podVerification.signatureFailReason')">
          {{ detailData.signatureReason || $t('podVerification.dash') }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('podVerification.photoResult')">
          <span :class="['tag', getTagClass(detailData.photoResult)]">{{
            getResultText(detailData.photoResult)
          }}</span>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('podVerification.photoFailReason')">
          {{ detailData.photoReason || $t('podVerification.dash') }}
        </el-descriptions-item>
        <el-descriptions-item :label="$t('podVerification.auditTime')">
          {{ detailData.auditTime || $t('podVerification.dash') }}
        </el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-button size="small" @click="detailDialogVisible = false">{{ $t('podVerification.close') }}
        </el-button>
      </div>
    </el-dialog>

    <!-- 成功提示 Toast -->
    <transition name="el-fade-in">
      <div v-if="successToastVisible" class="toast">
        <div class="toast-icon">✓</div>
        <div>
          <div class="toast-title">{{ $t('podVerification.taskCreated') }}</div>
          <div class="toast-desc">
            {{ $t('podVerification.taskCreatedDesc', { count: formatNumber(submittedCount) }) }}
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import {
  podManualReviewExportAudit,
  podManualReviewExportPreCheck,
  podManualReviewPage,
  podManualReviewPageAuditResult,
  podManualReviewSubmitAudit,
  podManualReviewUpload
} from '@/api/newExpressAPI';
import { goRWExportList } from '@/utils/goExportList';

export default {
  name: 'DesignatedAudit',
  data() {
    return {
      // 上传文件信息
      fileInfo: {
        name: ''
      },
      // 预提交校验状态
      precheckState: this.$t('podVerification.waitPrecheck'),
      precheckStateColor: '#8d98a6',
      showPrecheck: false,
      canStartAudit: false,
      submitting: false,
      // 预提交校验返回的任务信息
      taskId: null,
      taskNo: '',
      uploadTotal: 0,
      // 预提交校验结果
      precheckData: {
        auditable: 0,
        notSigned: 0,
        noPOD: 0,
        notExist: 0,
        duplicate: 0
      },

      // 任务列表查询
      taskQuery: {
        taskNo: '',
        status: '',
        pageNumber: 1,
        pageSize: 10
      },
      // 任务列表数据
      taskList: [],
      taskTotal: 0,
      taskLoading: false,

      // 查看结果弹窗
      resultDialogVisible: false,
      currentTaskNo: '',
      currentTaskId: null,
      resultQuery: {
        keyword: '',
        result: '',
        pageNumber: 1,
        pageSize: 10
      },
      resultList: [],
      resultTotal: 0,
      resultLoading: false,
      // 关键字输入防抖定时器
      keywordTimer: null,
      // 上一次已发起搜索的关键字（用于去重）
      lastKeyword: '',

      // 详情弹窗
      detailDialogVisible: false,
      detailData: {},
      // 详情弹窗 label 列宽，避免"签名不通过原因"等长标签折行
      detailLabelStyle: {
        width: '120px'
      },

      // 成功 Toast
      successToastVisible: false,
      submittedCount: 0,
      toastTimer: null
    };
  },
  created() {
    this.getTaskList();
  },
  beforeDestroy() {
    clearTimeout(this.keywordTimer);
    clearTimeout(this.toastTimer);
  },
  methods: {
    // 下载模板
    downloadTemplate() {
      const data = '运单号\nAJ260819127404701\nAJ260804136875901\n';
      const blob = new Blob(['\ufeff' + data], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = this.$t('podVerification.templateFileName');
      a.click();
      URL.revokeObjectURL(url);
    },

    // 选择文件后自动上传并预提交校验
    handleFileChange(file) {
      if (!file || !file.name) return;
      this.fileInfo.name = file.name;
      this.showPrecheck = false;
      this.canStartAudit = false;
      this.taskId = null;
      this.taskNo = '';
      this.precheckState = this.$t('podVerification.prechecking');
      this.precheckStateColor = '#3c7bce';

      const formData = new FormData();
      formData.append('file', file.raw);
      podManualReviewUpload(formData)
        .then((res) => {
          if (res.code === 200) {
            const data = res.data || {};
            this.taskId = data.taskId;
            this.taskNo = data.taskNo;
            this.uploadTotal = data.uploadTotal || 0;
            this.precheckData = {
              auditable: data.auditTotal || 0,
              notSigned: data.notSigned || 0,
              noPOD: data.signedNoPod || 0,
              notExist: data.notExist || 0,
              duplicate: data.duplicate || 0
            };
            this.precheckState = this.$t('podVerification.precheckDone');
            this.precheckStateColor = '#10a44d';
            this.showPrecheck = true;
            this.canStartAudit = this.precheckData.auditable > 0;
          }
        })
        .catch(() => {
          this.onPrecheckFail();
        })
        .finally(() => {
          this.$nextTick(() => {
            if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles();
          });
        });
    },

    // 预提交校验失败处理
    onPrecheckFail() {
      this.showPrecheck = false;
      this.canStartAudit = false;
      this.taskId = null;
      this.taskNo = '';
      this.precheckState = this.$t('podVerification.precheckFailed');
      this.precheckStateColor = '#df4b4b';
    },

    // 重新选择文件
    resetFile() {
      this.fileInfo.name = '';
      this.showPrecheck = false;
      this.canStartAudit = false;
      this.submitting = false;
      this.taskId = null;
      this.taskNo = '';
      this.uploadTotal = 0;
      this.precheckData = {
        auditable: 0,
        notSigned: 0,
        noPOD: 0,
        notExist: 0,
        duplicate: 0
      };
      this.precheckState = this.$t('podVerification.waitPrecheck');
      this.precheckStateColor = '#8d98a6';
      if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles();
    },

    // 导出预校验明细
    exportPrecheckDetail() {
      if (!this.taskId) return;
      podManualReviewExportPreCheck(this.taskId).then((res) => {
        if (res.code === 200) {
          goRWExportList(this);
        }
      });
    },

    // 开始审核 -> 提交审核
    startAudit() {
      if (!this.taskId || this.submitting) return;
      this.submitting = true;
      const auditCount = this.precheckData.auditable;
      podManualReviewSubmitAudit(this.taskId)
        .then((res) => {
          if (res.code === 200) {
            this.submittedCount = auditCount;
            this.canStartAudit = false;
            this.successToastVisible = true;
            clearTimeout(this.toastTimer);
            this.toastTimer = setTimeout(() => {
              this.successToastVisible = false;
            }, 3200);
            this.getTaskList();
          }
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    // 任务列表查询
    getTaskList() {
      const data = {
        pageSize: this.taskQuery.pageSize,
        pageNumber: this.taskQuery.pageNumber,
        params: {
          status: this.taskQuery.status || null,
          taskNo: this.taskQuery.taskNo || null
        }
      };
      this.taskLoading = true;
      podManualReviewPage(data)
        .then((res) => {
          if (res.code === 200) {
            const result = res.data || {};
            this.taskList = result.records || [];
            this.taskTotal = result.total || 0;
          }
        })
        .finally(() => {
          this.taskLoading = false;
        });
    },

    // 任务列表搜索
    searchTasks() {
      this.taskQuery.pageNumber = 1;
      this.getTaskList();
    },

    // 任务列表重置
    resetTaskQuery() {
      this.taskQuery = {
        taskNo: '',
        status: '',
        pageNumber: 1,
        pageSize: this.taskQuery.pageSize
      };
      this.getTaskList();
    },

    // 任务列表每页数量变化
    handleTaskSizeChange(val) {
      this.taskQuery.pageNumber = 1;
      this.taskQuery.pageSize = val;
      this.getTaskList();
    },

    // 查看结果
    showResult(row) {
      if (!row.taskId) {
        this.$message.warning(this.$t('podVerification.noTaskIdView'));
        return;
      }
      this.currentTaskNo = row.taskNo;
      this.currentTaskId = row.taskId;
      // 切换任务时清理上一个任务残留的防抖定时器与关键字记录
      clearTimeout(this.keywordTimer);
      this.lastKeyword = '';
      this.resultQuery = {
        keyword: '',
        result: '',
        pageNumber: 1,
        pageSize: this.resultQuery.pageSize
      };
      this.resultDialogVisible = true;
      this.getResultList();
    },

    // 组装审核结果查询参数
    buildResultParams() {
      // 接口 auditResult：1=合格 0=不合格（可空）；"待审核"接口暂未支持，暂不传筛选值
      const auditResult =
        this.resultQuery.result === 0 || this.resultQuery.result === 1
          ? this.resultQuery.result
          : null;
      return {
        waybillNumber: this.resultQuery.keyword || null,
        taskId: this.currentTaskId,
        auditResult
      };
    },

    // 查看审核结果列表
    getResultList() {
      const data = {
        pageSize: this.resultQuery.pageSize,
        pageNumber: this.resultQuery.pageNumber,
        params: this.buildResultParams()
      };
      this.resultLoading = true;
      podManualReviewPageAuditResult(data)
        .then((res) => {
          if (res.code === 200) {
            const result = res.data || {};
            this.resultList = result.records || [];
            this.resultTotal = result.total || 0;
          }
        })
        .finally(() => {
          this.resultLoading = false;
        });
    },

    // 关键字输入防抖：停止输入 400ms 后自动搜索
    handleKeywordInput(val) {
      clearTimeout(this.keywordTimer);
      this.keywordTimer = setTimeout(() => {
        this.triggerKeywordSearch(val);
      }, 400);
    },

    // 回车：立即搜索，同时取消待执行的防抖，避免重复请求
    handleKeywordSearchNow() {
      clearTimeout(this.keywordTimer);
      this.triggerKeywordSearch(this.resultQuery.keyword);
    },

    // 统一入口：关键字未变化时不重复请求
    triggerKeywordSearch(val) {
      const keyword = (val || '').trim();
      if (keyword === this.lastKeyword) return;
      this.lastKeyword = keyword;
      this.handleResultFilter();
    },

    // 结果弹窗筛选
    handleResultFilter() {
      this.resultQuery.pageNumber = 1;
      this.getResultList();
    },

    // 结果弹窗每页数量变化
    handleResultSizeChange(val) {
      this.resultQuery.pageNumber = 1;
      this.resultQuery.pageSize = val;
      this.getResultList();
    },

    // 导出审核结果（结果弹窗，带筛选条件）
    exportResults() {
      podManualReviewExportAudit(this.buildResultParams()).then((res) => {
        if (res.code === 200) {
          goRWExportList(this);
        }
      });
    },

    // 导出审核结果（任务列表行，仅导出已审核完成的单号）
    exportTaskResult(row) {
      if (!row.taskId) {
        this.$message.warning(this.$t('podVerification.noTaskIdExport'));
        return;
      }
      podManualReviewExportAudit({
        waybillNumber: null,
        taskId: row.taskId,
        auditResult: null
      }).then((res) => {
        if (res.code === 200) {
          goRWExportList(this);
        }
      });
    },

    // 打开详情
    openDetail(row) {
      this.detailData = row;
      this.detailDialogVisible = true;
    },

    // 计算综合审核结果（返回内部标识，展示时用 getResultText 翻译）
    getTotalResult(row) {
      if (!row) return '待审核';
      if (
        !row.signatureResult ||
        !row.photoResult ||
        row.signatureResult === '待审核' ||
        row.photoResult === '待审核'
      ) {
        return '待审核';
      }
      return row.signatureResult === '合格' && row.photoResult === '合格' ? '合格' : '不合格';
    },

    // 翻译审核结果值（合格/不合格/待审核）
    getResultText(result) {
      const map = {
        合格: this.$t('podVerification.qualified'),
        不合格: this.$t('podVerification.unqualified'),
        待审核: this.$t('podVerification.pending')
      };
      return map[result] || result || this.$t('podVerification.dash');
    },

    // 翻译任务状态值（审核中/已完成）
    getStatusText(status) {
      const map = {
        审核中: this.$t('podVerification.auditing'),
        已完成: this.$t('podVerification.completed')
      };
      return map[status] || status;
    },

    // 组装不通过原因
    getFailReason(row) {
      if (!row) return this.$t('podVerification.dash');
      const reasons = [];
      if (row.signatureResult === '不合格' && row.signatureReason) {
        reasons.push(this.$t('podVerification.signaturePrefix') + row.signatureReason);
      }
      if (row.photoResult === '不合格' && row.photoReason) {
        reasons.push(this.$t('podVerification.photoPrefix') + row.photoReason);
      }
      return reasons.length
        ? reasons.join(this.$t('podVerification.reasonSeparator'))
        : this.$t('podVerification.dash');
    },

    // 格式化数字（千分位）
    formatNumber(num) {
      if (!num && num !== 0) return '0';
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },

    // 获取标签 CSS 类
    getTagClass(result) {
      const map = {
        合格: 'pass',
        不合格: 'fail',
        通过: 'pass',
        不通过: 'fail',
        待审核: 'pending'
      };
      return map[result] || '';
    }
  }
};
</script>

<style scoped>
.designated-audit-container {
  padding: 10px 0;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #303846;
}

.simple-tip {
  font-size: 12px;
  color: #7c8796;
  line-height: 1.7;
  margin-bottom: 12px;
}

/* 上传卡片 */
.upload-card {
  border: 1px solid #e6ebf0;
  border-radius: 9px;
  padding: 16px;
  background: #fbfcfd;
}

.upload-line {
  display: flex;
  align-items: center;
  gap: 12px;
}

.upload-main {
  flex: 1;
}

.upload-title {
  font-size: 13px;
  font-weight: 700;
}

.upload-desc {
  font-size: 11px;
  color: #8d98a6;
  margin-top: 5px;
}

.file-info {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #edf1f4;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.file-name {
  font-size: 12px;
  font-weight: 600;
}

.file-meta {
  font-size: 11px;
  color: #8d98a6;
  margin-top: 3px;
}

.precheck-state {
  font-size: 11px;
}

/* 预提交校验结果 */
.precheck-box {
  margin-top: 14px;
  border-top: 1px solid #edf1f4;
  padding-top: 14px;
}

.precheck-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.precheck-title strong {
  font-size: 13px;
}

.precheck-tip {
  font-size: 11px;
  color: #8d98a6;
}

.precheck-stats {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
}

.precheck-stat {
  border: 1px solid #e6ebf0;
  border-radius: 8px;
  padding: 10px 12px;
  background: #fff;
}

.precheck-stat .label {
  font-size: 11px;
  color: #8792a0;
}

.precheck-stat .value {
  font-size: 20px;
  font-weight: 700;
  margin-top: 4px;
}

.precheck-stat.ok .value {
  color: #12a953;
}

.precheck-stat.warn .value {
  color: #e58b24;
}

.precheck-stat.bad .value {
  color: #df4b4b;
}

.precheck-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
}

.precheck-detail {
  margin-top: 12px;
  border: 1px solid #e9edf2;
  border-radius: 8px;
  overflow: auto;
}

/* 任务工具栏 */
.task-toolbar {
  display: flex;
  align-items: flex-end;
  gap: 10px;
  margin-bottom: 12px;
}

/* 进度条 */
.progress-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 状态标签 */
.status {
  display: inline-flex;
  padding: 3px 8px;
  border-radius: 10px;
  font-size: 11px;
}

.status.green {
  background: #eaf9f0;
  color: #10a44d;
}

.status.blue {
  background: #edf5ff;
  color: #3c7bce;
}

.status.red {
  background: #fff0f0;
  color: #df4b4b;
}

.status.gray {
  background: #f1f3f5;
  color: #7c8796;
}

/* 标签 */
.tag {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 4px;
  font-size: 11px;
}

.tag.pending {
  background: #fff0f0;
  color: #ef5151;
  border: 1px solid #ffd1d1;
}

.tag.pass {
  background: #ebfaf1;
  color: #14a84e;
  border: 1px solid #cbeed9;
}

.tag.fail {
  background: #fff0f0;
  color: #e34444;
  border: 1px solid #ffd2d2;
}

/* Toast */
.toast {
  position: fixed;
  top: 76px;
  right: 28px;
  z-index: 100;
  min-width: 310px;
  max-width: 420px;
  background: #fff;
  border: 1px solid #dce8e1;
  border-radius: 9px;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.14);
  padding: 13px 16px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.toast-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #eaf9f0;
  color: #10a44d;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 700;
}

.toast-title {
  font-size: 13px;
  font-weight: 700;
  color: #303846;
  line-height: 1.4;
}

.toast-desc {
  font-size: 11px;
  color: #7d8897;
  margin-top: 3px;
  line-height: 1.5;
}

/* 结果弹窗头部 */
.result-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.result-filter {
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 分页 */
.pager {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  font-size: 11px;
  color: #8b96a4;
}
</style>
